import { Injectable, OnDestroy } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, EMPTY, forkJoin, from, iif, of, Subject } from "rxjs";
import { catchError, map, mergeMap, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { InspectionImageCreate, InspectionImageInfo, ReadImageOption, UploadOption } from "../_objects/inspection-image";
import { TenantDataFile, TenantDataFileFolderResponse } from "../_objects/tenant-data";
import { UploadProgressDialogComponent } from "../inspection/dialogs/upload-progress-dialog/upload-progress-dialog.component";
import { UploadProgressDialogService } from "../inspection/dialogs/upload-progress-dialog/upload-progress-dialog.service";
import { BlobClient } from "./blob.service";
import { CommonSnackBarService } from "./common-snack-bar.service";
import { InspectionImageService } from "./http-api/inspection-image.service";
import { ImageConflictResolverService } from "./image-upload/image-conflict-resolver.service";
import { FileList, ImageCreationMapper } from "./image-upload/image-creation.mapper";
import { NetworkStatusService } from "./network-status.service";
import { CreateImagesResponse } from "./responses/inspection-image.response";
import { TenantDataService } from "./tenant-data.service";

type UploadStatus = { totalFileCount: number; uploadedFileCount: number };
const initUploadStatus: UploadStatus = { totalFileCount: 0, uploadedFileCount: 0 };

@Injectable({
  providedIn: "root"
})
export class ImageUploadService implements OnDestroy {
  public progressDialogRef: MatDialogRef<UploadProgressDialogComponent>;

  private onCancel$ = new Subject();
  private onDestroy$ = new Subject();

  private uploadingStatus$: BehaviorSubject<UploadStatus> = new BehaviorSubject<UploadStatus>(initUploadStatus);
  private incrementUploadedFile$ = () =>
    this.uploadingStatus$.next({ ...this.uploadingStatus$.value, uploadedFileCount: this.uploadingStatus$.value.uploadedFileCount + 1 });
  public uploadCompleted$ = new Subject<void>();

  constructor(
    private snackBar: CommonSnackBarService,
    private inspectionImageService: InspectionImageService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private networkStatusService: NetworkStatusService,
    private uploadProgressDialogService: UploadProgressDialogService,
    private conflictResolutionService: ImageConflictResolverService,
    private tenantDataService: TenantDataService
  ) {}

  ngOnDestroy(): void {
    this.onCancel$.next(undefined);
    this.onCancel$.unsubscribe();
    this.onDestroy$.next(undefined);
    this.onDestroy$.subscribe();
  }

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  private get isOffline() {
    return this.networkStatusService.isOffline;
  }

  public async uploadInspectionFiles(inspectionId: string, locationName: string, files: File[]) {
    const creationsImages = await ImageCreationMapper.toImagesCreation(files, inspectionId, locationName);
    this.uploadFiles(inspectionId, creationsImages);
  }

  public async uploadInspectionFolder(inspectionId: string, filesList: FileList[]) {
    const images = await ImageCreationMapper.toImagesCreationFromFileLists(filesList, inspectionId);
    this.uploadFiles(inspectionId, images);
    return this.uploadCompleted$;
  }

  public uploadInspectionFileFromMyData(inspectionId: string, tenantDataFiles: TenantDataFile[], isDeletedWithUploading: boolean = false) {
    const inspectionImages = tenantDataFiles.map((file) => ImageCreationMapper.toImageCreationFromTenantDataFile(file, inspectionId));
    this.initProgressTracking(inspectionImages.length);
    this.createImagesFromMyData(inspectionId, inspectionImages, tenantDataFiles, isDeletedWithUploading);
  }

  private uploadFiles(inspectionId: string, creationsImages: InspectionImageCreate[]) {
    this.initProgressTracking(creationsImages.length);

    this.createInspectionImages(inspectionId, creationsImages);
  }

  private createInspectionImages(inspectionId: string, imagesToCreate: InspectionImageCreate[]) {
    this.inspectionImageService
      .createInspectionImages(inspectionId, imagesToCreate)
      .pipe(
        catchError(() => {
          this.failureUpload();
          return of({ imageConflicts: [], createdImages: [] });
        })
      )
      .subscribe((response) => {
        if (response.imageConflicts.length > 0) {
          this.resolveConflicts(response, imagesToCreate, (resolved) => this.createInspectionImages(inspectionId, resolved));
        }
        this.uploadImageToBlobUrl(imagesToCreate, response.createdImages).subscribe({
          error: () => this.failureUpload()
        });
      });
  }

  private failureUpload(): void {
    this.uploadProgressDialogService.failureFileCount =
      this.uploadProgressDialogService.totalFileCount - this.uploadProgressDialogService.completeFileCount;
    this.uploadProgressDialogService.successFileCount = this.uploadProgressDialogService.completeFileCount;
    this.isOffline
      ? this.snackBar.open(this.translate.instant("dataUploadDialog.errorOccurredSave"))
      : this.snackBar.open(this.translate.instant("dataUploadDialog.errorOccurredUpload"));

    this.uploadProgressDialogService.isError = true;
  }

  private initProgressTracking(total: number) {
    this.openProgressDialog(total);
    this.uploadingStatus$.next({ totalFileCount: total, uploadedFileCount: 0 });

    this.uploadingStatus$.pipe(takeUntil(this.uploadCompleted$)).subscribe((status) => {
      this.uploadProgressDialogService.completeFileCount = status.uploadedFileCount;
      if (status.uploadedFileCount == this.uploadProgressDialogService.totalFileCount) {
        this.progressDialogRef.close();
        this.uploadCompleted$.next();
      }
    });
  }

  private resolveConflicts(
    response: CreateImagesResponse,
    inspectionImageCreate: InspectionImageCreate[],
    onResolved: (resolved: InspectionImageCreate[]) => void
  ) {
    const filterOutSkippedImages = (images: InspectionImageCreate[]) => images.filter((image) => image.uploadOption != UploadOption.Skip);
    const skippedImages = (images: InspectionImageCreate[]) => images.filter((image) => image.uploadOption == UploadOption.Skip);

    this.conflictResolutionService
      .resolveConflicts(response.imageConflicts, inspectionImageCreate)
      .pipe(take(1))
      .pipe(
        tap(
          (resolved) =>
            (this.uploadProgressDialogService.totalFileCount = this.uploadingStatus$.value.totalFileCount - skippedImages(resolved).length)
        )
      )
      .pipe(map(filterOutSkippedImages))
      .subscribe((resolved) => {
        if (resolved.length > 0) {
          onResolved(resolved);
        } else {
          this.uploadCompleted$.next();
          this.progressDialogRef.close();
        }
      });
  }

  private async processAllBatches(batchedItems: InspectionImageInfo[][], images: InspectionImageCreate[]): Promise<string[]> {
    const results: string[] = [];
    for (const batch of batchedItems) {
      const batchResult = await this.processBatch(batch, images); // Process one batch at a time
      results.push(...batchResult); // Store batch results
    }
    return results;
  }
  private processBatch(batch: InspectionImageInfo[], images: InspectionImageCreate[]): Promise<any[]> {
    return Promise.all(batch.map(async (i) => await this.sendToBlob(i, images)));
  }

  private async sendToBlob(info: InspectionImageInfo, images: InspectionImageCreate[]) {
    const image = images.find((f) => f.fileName == info.originalFileName && f.folderName == info.folderName);

    if (info.uploadAudioUrl) {
      await this.uploadAudioFile(info.uploadAudioUrl, image.audioFile);
    }

    return await BlobClient.create(info.uploadUrl)
      .uploadData(image.file, {
        blockSize: 4 * 1024 * 1024, // 4MB
        maxSingleShotSize: 4 * 1024 * 1024,
        concurrency: 1,
        blobHTTPHeaders: { blobContentType: image.file.type }
      })
      .then(() => this.incrementUploadedFile$());
  }

  public uploadImageToBlobUrl(images: InspectionImageCreate[], uploadUrls: InspectionImageInfo[]) {
    const batches = batch(uploadUrls, 10);
    const result = this.processAllBatches(batches, images);

    return from(result);
  }

  private async uploadAudioFile(uploadAudioUrl: string, audioFile: File) {
    await BlobClient.create(uploadAudioUrl).uploadData(audioFile, {
      blockSize: 4 * 1024 * 1024, // 4MB
      maxSingleShotSize: 4 * 1024 * 1024,
      concurrency: 20,
      blobHTTPHeaders: { blobContentType: audioFile.type }
    });
  }

  private openProgressDialog(total: number): void {
    this.uploadProgressDialogService.init();

    this.uploadProgressDialogService.totalFileCount = total;
    this.progressDialogRef = this.dialog.open(UploadProgressDialogComponent, {
      width: UploadProgressDialogComponent.defaultDialogWidth,
      maxHeight: UploadProgressDialogComponent.defaultDialogMaxHeight,
      minHeight: UploadProgressDialogComponent.defaultDialogMinHeight,
      disableClose: true
    });

    this.progressDialogRef.componentInstance.dialogClose.subscribe((_) => {
      if (this.uploadProgressDialogService.completeFileCount !== total) {
        this.uploadProgressDialogService.isError = true;
      } else {
        this.progressDialogRef.close(true);
      }
    });
  }

  private deleteTenantDataFiles(tenantDataFiles: TenantDataFile[]) {
    const files = tenantDataFiles.map((tenantData) => {
      const file = new TenantDataFileFolderResponse();
      file.id = tenantData.id;
      file.displayType = "File";
      return file;
    });
    return this.tenantDataService.deleteFileFolder(files);
  }

  private createImagesFromMyData(
    inspectionId: string,
    inspectionImages: InspectionImageCreate[],
    tenantData: TenantDataFile[],
    isDeletedWithUploading: boolean = false
  ) {
    const readInspectionImages = (image: InspectionImageInfo) =>
      this.inspectionImageService
        .readInspectionImage(inspectionId, image.imageId, ReadImageOption.Upload)
        .pipe(tap(() => this.incrementUploadedFile$()));

    const handleConflicts = (res: CreateImagesResponse) => {
      if (res.imageConflicts.length > 0) {
        this.resolveConflicts(res, inspectionImages, (resolved) =>
          this.createImagesFromMyData(inspectionId, resolved, tenantData, isDeletedWithUploading)
        );
      }
    };

    const deleteTenantDataFiles = iif(() => isDeletedWithUploading, this.deleteTenantDataFiles(tenantData), EMPTY);

    // there should be no XML Waygate data files in the tenant data, using empty array
    this.inspectionImageService
      .createInspectionImages(inspectionId, inspectionImages)
      .pipe(
        tap(handleConflicts),
        mergeMap((response) => forkJoin(response.createdImages.map(readInspectionImages))),
        catchError(() => {
          this.failureUpload();
          return of([]);
        }),
        switchMap(() => deleteTenantDataFiles)
      )
      .subscribe();
  }
}

function batch<T>(array: T[], batchSize: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += batchSize) {
    result.push(array.slice(i, i + batchSize));
  }
  return result;
}
